import { api } from './api';

export type CustomerOrderStatus = 'NEW' | 'ORDERED' | 'RECEIVED' | 'NOTIFIED' | 'DELIVERED' | 'CANCELLED';

export interface CustomerOrder {
  id: string;
  orderNumber: string;
  customerId: string;
  brandId: string | null;
  modelId: string | null;
  itemName: string | null;
  quantity: number;
  expectedPrice: number | null;
  advanceAmount: number;
  status: CustomerOrderStatus;
  notes: string | null;
  internalNotes: string | null;
  whatsappNotifiedAt: string | null;
  orderedAt: string | null;
  receivedAt: string | null;
  notifiedAt: string | null;
  deliveredAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  serviceId: string | null;
  branchId: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  customer: { id: string; name: string; phone: string; whatsappNumber: string | null; email: string | null };
  brand: { id: string; name: string } | null;
  model: { id: string; name: string } | null;
  branch?: { id: string; name: string; code: string };
  service: { id: string; ticketNumber: string; status: string } | null;
  createdBy: { id: string; name: string; email: string | null };
  whatsappNotifier: { id: string; name: string } | null;
  statusHistory?: Array<{
    id: string;
    status: CustomerOrderStatus;
    notes: string | null;
    createdAt: string;
    changedByUser: { id: string; name: string } | null;
  }>;
}

export interface CustomerOrderFilters {
  page?: number;
  limit?: number;
  branchId?: string;
  status?: CustomerOrderStatus;
  search?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CustomerOrderStats {
  new: number;
  ordered: number;
  received: number;
  notified: number;
  delivered: number;
  cancelled: number;
  active: number;
  awaitingPickup: number;
  deliveredThisMonth: number;
  total: number;
}

export interface TodayBranchStat {
  branchId: string;
  branchName: string;
  branchCode: string;
  todayNewCount: number;
  todayTotalCount: number;
}

export const customerOrderApi = {
  getAll: async (
    filters?: CustomerOrderFilters
  ): Promise<{
    customerOrders: CustomerOrder[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> => {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.branchId) params.append('branchId', filters.branchId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);
    const response = await api.get(`/customer-orders?${params.toString()}`);
    return response.data.data;
  },

  getById: async (id: string): Promise<CustomerOrder> => {
    const response = await api.get(`/customer-orders/${id}`);
    return response.data.data;
  },

  updateStatus: async (
    id: string,
    status: CustomerOrderStatus,
    notes?: string,
    cancelReason?: string
  ): Promise<CustomerOrder> => {
    const response = await api.patch(`/customer-orders/${id}/status`, { status, notes, cancelReason });
    return response.data.data;
  },

  getStats: async (branchId?: string): Promise<CustomerOrderStats> => {
    const params = new URLSearchParams();
    if (branchId) params.append('branchId', branchId);
    const response = await api.get(`/customer-orders/stats?${params.toString()}`);
    return response.data.data;
  },

  getTodayStatsByBranch: async (): Promise<TodayBranchStat[]> => {
    const response = await api.get('/customer-orders/stats/today-by-branch');
    return response.data.data;
  },

  exportToExcel: async (branchId?: string): Promise<Blob> => {
    const params = new URLSearchParams();
    if (branchId) params.append('branchId', branchId);
    const response = await api.get(`/customer-orders/export?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};
